import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Sparkles, Disc3, Coins, Tag } from "lucide-react";
import TopBar from "@/components/app/TopBar";
import Sidebar from "@/components/app/Sidebar";
import ArtistHero from "@/components/music/ArtistHero";
import TrackRow from "@/components/music/TrackRow";
import UploadModal from "@/components/music/UploadModal";
import { fmtNumber } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Music() {
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [activeArtistId, setActiveArtistId] = useState(null);
  const [tab, setTab] = useState("popular");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    try {
      const [a, t] = await Promise.all([
        axios.get(`${API}/music/artists`),
        axios.get(`${API}/music/tracks?limit=100`),
      ]);
      setArtists(a.data);
      setTracks(t.data);
      if (!activeArtistId && a.data.length) setActiveArtistId(a.data[0].id);
    } catch {
      toast.error("No se pudo cargar la biblioteca musical");
    }
  };

  useEffect(() => { load(); }, []);

  const activeArtist = artists.find((a) => a.id === activeArtistId);
  const artistTracks = useMemo(
    () => tracks.filter((t) => t.artist_id === activeArtistId),
    [tracks, activeArtistId]
  );

  const tabbedTracks = useMemo(() => {
    if (tab === "popular") return [...tracks].sort((a, b) => b.plays - a.plays).slice(0, 12);
    if (tab === "horizonte") return tracks.filter((t) => t.promoted_horizonte);
    if (tab === "nuevo") return [...tracks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 12);
    return tracks;
  }, [tab, tracks]);

  const promote = async () => {
    if (!artistTracks.length) return;
    const top = artistTracks[0];
    try {
      await axios.post(`${API}/music/tracks/${top.id}/promote`);
      toast.success(`"${top.title}" promocionado en Modo Horizonte`);
      load();
    } catch {
      toast.error("No se pudo promocionar");
    }
  };

  return (
    <div data-testid="music-page" className="bg-cream min-h-screen text-ink pb-28">
      <TopBar />
      <div className="max-w-[1600px] mx-auto flex">
        <Sidebar />

        <main className="flex-1 min-w-0 px-4 md:px-6 lg:px-8 py-5">
          <div className="max-w-[1100px] mx-auto">
            {/* Header strip */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Obras Musicales</p>
                <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tighter leading-none">
                  Donde la música <span className="italic">paga sola</span>.
                </h1>
                <p className="mt-2 text-sm text-ink/70 max-w-xl">
                  Sube tu música original, construye tu perfil de artista y deja que el Modo Horizonte la lleve al mundo. Reproducciones, propinas, licencias y merch en un solo flujo.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="music-upload-btn"
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 rounded-full text-sm hover:bg-coral transition"
                >
                  <UploadCloud className="w-4 h-4" />
                  Subir música
                </button>
              </div>
            </div>

            {/* Earnings strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <Stat icon={Disc3} k={fmtNumber(tracks.reduce((a, t) => a + t.plays, 0))} v="reproducciones totales" />
              <Stat icon={Coins} k={`$${tracks.reduce((a, t) => a + t.tips_total, 0).toFixed(0)}`} v="propinas a artistas" testid="music-stat-tips" />
              <Stat icon={Sparkles} k={String(tracks.filter(t=>t.promoted_horizonte).length).padStart(2,"0")} v="en Horizonte" />
              <Stat icon={Tag} k={`$${tracks.reduce((a, t) => a + t.license_price, 0).toFixed(0)}`} v="licencias disponibles" />
            </div>

            {/* Featured artist */}
            {activeArtist && (
              <ArtistHero artist={activeArtist} tracks={artistTracks} onPromote={promote} />
            )}

            {/* Artists strip */}
            <div className="mb-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-3">Artistas en la plataforma</p>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {artists.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`artist-chip-${a.id}`}
                    onClick={() => setActiveArtistId(a.id)}
                    className={`shrink-0 flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-full border transition ${
                      activeArtistId === a.id ? "bg-ink text-cream border-ink" : "bg-white border-ink/10 hover:border-coral"
                    }`}
                  >
                    <img src={a.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <span className="text-sm">{a.name}</span>
                    <span className={`font-mono text-[10px] ${activeArtistId === a.id ? "text-cream/55" : "text-ink-mid"}`}>
                      {fmtNumber(a.monthly_listeners)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Discography */}
            <div className="border hairline bg-white rounded-2xl p-3 md:p-5 mb-10">
              <div className="flex items-center justify-between mb-3 px-2">
                <h3 className="font-serif text-2xl tracking-tight">
                  {activeArtist ? `Discografía · ${activeArtist.name}` : "Discografía"}
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                  {artistTracks.length} obras
                </span>
              </div>
              <div className="grid grid-cols-12 gap-3 px-3 pb-2 border-b hairline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                <div className="col-span-1 hidden sm:block text-center">#</div>
                <div className="col-span-7 sm:col-span-5">Obra</div>
                <div className="col-span-3 hidden sm:block">Género</div>
                <div className="col-span-2 sm:col-span-1 text-right">Plays</div>
                <div className="col-span-2 sm:col-span-1 text-right">Propinas</div>
              </div>
              <div className="mt-2">
                {artistTracks.length === 0 ? (
                  <p className="text-sm text-ink-mid p-6 text-center">Aún no hay obras de este artista.</p>
                ) : (
                  artistTracks.map((t, i) => (
                    <TrackRow key={t.id} track={t} list={artistTracks} index={i} />
                  ))
                )}
              </div>
            </div>

            {/* Discover */}
            <div className="border hairline bg-white rounded-2xl p-3 md:p-5">
              <div className="flex items-center justify-between mb-3 px-2">
                <h3 className="font-serif text-2xl tracking-tight">Descubrir</h3>
                <div className="flex gap-1">
                  {[
                    { id: "popular",    label: "Populares" },
                    { id: "horizonte",  label: "Horizonte" },
                    { id: "nuevo",      label: "Nuevo" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      data-testid={`music-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      className={`px-3 py-1.5 rounded-full text-xs transition ${
                        tab === t.id ? "bg-coral text-cream" : "text-ink-mid hover:bg-cream-deep/60"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                {tabbedTracks.map((t, i) => (
                  <TrackRow key={`${tab}-${t.id}`} track={t} list={tabbedTracks} index={i} />
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        artists={artists}
        onCreated={() => load()}
      />
    </div>
  );
}

function Stat({ icon: Icon, k, v, testid }) {
  return (
    <div data-testid={testid} className="border hairline bg-white rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-coral" strokeWidth={1.7} />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">{v}</p>
      </div>
      <p className="font-serif text-3xl tracking-tighter">{k}</p>
    </div>
  );
}
